import React from 'react';
import { usePermissions } from '../hooks/usePermissions';
import { useAuth } from '../auth/AuthContext';

const normalizeRole = (role) => (role || '').toString().toLowerCase();

const getUserRole = (user) =>
  normalizeRole(user?.role || user?.Role || user?.['http://schemas.microsoft.com/ws/2008/06/identity/claims/role']);

const getUserOrganizationRole = (user) =>
  normalizeRole(user?.organizationRole || user?.OrganizationRole);

/**
 * Permission gate component that conditionally renders children based on user permissions
 */
export default function PermissionGate({ 
  permission, 
  permissions, 
  requireAll = false, 
  fallback = null, 
  children 
}) {
  const { user } = useAuth();
  const { can, canAny, canAll, loading } = usePermissions(
    user?.sub, // userId from JWT token
    user?.role, 
    user?.permissions,
    user?.organizationReferenceId
  );

  // Show loading state if permissions are still being loaded
  if (loading) {
    return fallback || <div className="animate-pulse bg-gray-200 h-4 rounded"></div>;
  }

  // Handle single permission check
  if (permission) {
    return can(permission) ? children : fallback;
  }

  // Handle multiple permissions check
  if (permissions && Array.isArray(permissions)) {
    const hasAccess = requireAll ? canAll(permissions) : canAny(permissions);
    return hasAccess ? children : fallback;
  }

  // If no permissions specified, render children (open access)
  return children;
}

/**
 * Higher-order component version of PermissionGate
 */
export const withPermissions = (
  WrappedComponent, 
  requiredPermissions, 
  options = {}
) => {
  const { requireAll = false, fallback = null } = options;

  return function PermissionWrappedComponent(props) {
    return (
      <PermissionGate
        permissions={requiredPermissions}
        requireAll={requireAll}
        fallback={fallback}
      >
        <WrappedComponent {...props} />
      </PermissionGate>
    );
  };
};

/**
 * Hook to get permission checker functions (alternative to PermissionGate)
 */
export const usePermissionCheck = () => {
  const { user } = useAuth();
  const { can, canAny, canAll, loading } = usePermissions(
    user?.sub, // userId from JWT token
    user?.role, 
    user?.permissions,
    user?.organizationReferenceId
  );
  const role = getUserRole(user);
  const organizationRole = getUserOrganizationRole(user);

  return {
    can,
    canAny,
    canAll,
    loading,
    isAdmin: role === 'admin' || organizationRole === 'admin',
    isMidwife: role === 'midwife' || organizationRole === 'midwife',
    isClient: role === 'client'
  };
};
